import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import axiosInstance from "@/lib/axios";
import { queryClient } from "@/lib/queryClient";
import { note_endpoint } from "@/config/endpoints";
import { mapLeadsInBoardCache } from "@/features/leads/api/useLeadMutations";
import { useChatNotesSync } from "@/features/leads/api/useLeadFormConfig";

/**
 * Rewrite (or drop, when `patch` returns null) note `noteId` wherever a cached
 * `["leads", ...]` row carries it. List/board rows hold only the latest
 * non-deleted note of the lead and of each deal (server `take: 1`, with id),
 * which the card/list preview reads (`pickLatestNote`). Returns whether any
 * cached row showed that note.
 */
type CachedNote = Record<string, unknown> & { id?: unknown };

function patchNoteInLeadCaches(
  noteId: number,
  patch: (note: CachedNote) => CachedNote | null
): boolean {
  const id = Number(noteId);
  if (!Number.isFinite(id)) return false;
  let hit = false;
  const isTarget = (n: CachedNote | null | undefined): n is CachedNote =>
    !!n && Number(n.id) === id;
  const mapNotes = (notes: unknown): unknown => {
    if (!Array.isArray(notes)) return notes;
    const list = notes as (CachedNote | null)[];
    if (!list.some(isTarget)) return notes;
    hit = true;
    return list.flatMap((n) => (isTarget(n) ? (patch(n) ?? []) : [n]));
  };
  mapLeadsInBoardCache(queryClient, (lead) => {
    if (!lead) return lead;
    const notes = mapNotes(lead.notes);
    let childChanged = false;
    const child = Array.isArray(lead.child)
      ? lead.child.map((c: { notes?: unknown } | null) => {
          if (!c) return c;
          const cn = mapNotes(c.notes);
          if (cn === c.notes) return c;
          childChanged = true;
          return { ...c, notes: cn };
        })
      : lead.child;
    if (notes === lead.notes && !childChanged) return lead;
    return { ...lead, notes, ...(childChanged ? { child } : {}) };
  });
  return hit;
}

/**
 * WhatsApp-style note moderation. The server enforces author-only edit and
 * author-or-admin delete; delete is always a soft delete (the thread keeps a
 * "This message was deleted" placeholder). Callers update local RHF state via
 * `applyNoteUpdate` on success — the invalidations here just keep the cached
 * lead/child detail payloads consistent for the next refetch.
 */
export function useNoteMutation() {
  // "Chat & notes sync": the note's chat copy is edited / deleted with it.
  // Open chats get that over the socket; this only marks cached threads stale
  // so a chat opened next refetches instead of showing the old words.
  const chatNotesSync = useChatNotesSync();
  const invalidateDetail = () => {
    queryClient.invalidateQueries({ queryKey: ["get-leads"] });
    queryClient.invalidateQueries({ queryKey: ["get-child"] });
    if (chatNotesSync) {
      queryClient.invalidateQueries({
        queryKey: ["internal-chat"],
        refetchType: "none",
      });
    }
  };

  const editNote = useMutation({
    mutationFn: async ({ id, content }: { id: number; content: string }) => {
      const res = await axiosInstance.patch(`${note_endpoint}/${id}`, {
        content,
      });
      return res.data;
    },
    onSuccess: (_data, { id, content }) => {
      invalidateDetail();
      // Card/list preview: patch the text in place (no heavy board refetch).
      patchNoteInLeadCaches(id, (n) => ({ ...n, content }));
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to update note");
    },
  });

  const deleteNote = useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const res = await axiosInstance.delete(`${note_endpoint}/${id}`);
      return res.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateDetail();
      // Only when a row was previewing this note: its fallback (the previous
      // note) isn't in the cache, so refetch the list/board views.
      if (patchNoteInLeadCaches(id, () => null)) {
        queryClient.invalidateQueries({ queryKey: ["leads"] });
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to delete note");
    },
  });

  return { editNote, deleteNote };
}
